/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useMemo, useState } from 'react';
import { mapModifiers } from 'libs/component';
import Default from 'components/templates/default';
import { Heading } from 'components/atoms/heading';
import { Section } from 'components/molecules/section';
import { Button } from 'components/atoms/button';
import { Text } from 'components/atoms/text';
import { Tabs } from 'components/molecules/tabs';
import { GalleryMasonry, GalleryMasonryItemProps } from 'components/molecules/gallery-masonry';
import { Image } from 'components/atoms/image';
import { Carousel } from 'components/organisms/carousel';
import { GALLERY_LIST, MEDIA_OPTION_LIST, NEWS_LIST } from 'libs/constants';
import { useEffect } from 'react';
import { NewsCard } from 'components/atoms/news-card';
import { useTranslation } from 'react-i18next';
import { OptionList } from 'components/molecules/option-list';

const heroImage = '/images/hero-banners/media.jpg';
const heroImageSp = '/images/hero-banners/media-sp.jpg';

export interface MediaProps {
  currentTab?: number;
  onOptionSelected?: (tab: number) => void;
  onNewsClick?: (slug: string) => void;
}

export const Media: React.FC<MediaProps> = ({
  currentTab: defaultCurrentTab = 0,
  onOptionSelected,
  onNewsClick,
}) => {
  const { t, i18n } = useTranslation();
  const [currentTab, setCurrentTab] = useState<number>(defaultCurrentTab);

  const optionList = useMemo(() => {
    return MEDIA_OPTION_LIST;
  }, []);

  const galleryList = useMemo(() => {
    return GALLERY_LIST;
  }, []);

  const newsList = useMemo(() => {
    return NEWS_LIST;
  }, []);

  // 仅移动端新闻轮播限制近 10 条；PC 保持全部
  const [isMobileNews, setIsMobileNews] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }
    const mq = window.matchMedia('(max-width: 992px)');
    const apply = () => setIsMobileNews(mq.matches);
    apply();
    if (mq.addEventListener) {
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
    mq.addListener(apply);
    return () => mq.removeListener(apply);
  }, []);

  const newsCarouselList = useMemo(() => {
    const list = newsList.filter((_: any, i: number) => i > 0);
    return isMobileNews ? list.slice(0, 10) : list;
  }, [newsList, isMobileNews]);

  useEffect(() => {
    setCurrentTab(defaultCurrentTab);
  }, [setCurrentTab, defaultCurrentTab, currentTab]);

  const handleOptionSelected = useCallback(
    (index: number) => {
      setCurrentTab(index);
      if (onOptionSelected) {
        onOptionSelected(index);
      }
    },
    [setCurrentTab, onOptionSelected]
  );

  const galleryTabs = galleryList.map(x => {
    return {
      children: (x.label as any)[i18n.language],
    };
  });

  const getGalleryItem = (items: any): GalleryMasonryItemProps[] => {
    return items.map((item: any) => {
      const outItem = {
        type: item.type,
        align: item.align,
        image: (
          <Image
            src={item.image.src}
            srcSp={item.image.srcSp}
            srcWebp={item.image.srcWebp}
            srcWebpSp={item.image.srcWebpSp}
            caption={item.image.caption}
            align={item.image.align}
            width="312"
            height="164"
          />
        ),
      };
      return outItem;
    });
  };

  const onGalleryTabChange = useCallback(() => {
    window.dispatchEvent(new Event('resize'));
  }, []);

  const optionItemList = optionList.map(x => (x.label as any)[i18n.language]);

  const handleNewsClick = useCallback(
    (slug: string) => {
      if (onNewsClick) {
        onNewsClick(slug);
      }
    },
    [onNewsClick]
  );

  const highlightNews = useMemo(() => {
    if (newsList.length > 0) {
      return newsList[0];
    }
    return undefined;
  }, [newsList]);

  return (
    <div className={mapModifiers('t-media', `lang-${i18n.language}`)}>
      <Default heroImage={heroImage} heroImageSp={heroImageSp}>
        {currentTab === 0 && <Heading>媒体</Heading>}
        {currentTab === 1 && <Heading>图片库</Heading>}
        {currentTab === 2 && <Heading>N.C.C集团</Heading>}

        <Section className="t-media__tabs">
          <OptionList
            currentIndex={currentTab}
            modifiers="horizontal"
            items={optionItemList}
            onSelected={handleOptionSelected}
          />
        </Section>

        {currentTab === 0 && (
          <div className={mapModifiers('t-media__description', 'news')}>
            <Text>
              <span
                dangerouslySetInnerHTML={{
                  __html: t('media.news.description'),
                }}
              />
            </Text>
          </div>
        )}

        {currentTab === 1 && (
          <div className={mapModifiers('t-media__description', 'gallery')}>
            <Text>
              <span
                dangerouslySetInnerHTML={{
                  __html: t('media.gallery.description'),
                }}
              />
            </Text>
          </div>
        )}

        {currentTab === 0 && (
          <Section className={mapModifiers('t-media__info', 'news')}>
            <div className="t-media__info-news">
              {highlightNews && (
                <div className="t-media__info-news-highlight">
                  <NewsCard
                    modifiers="large"
                    value={highlightNews.slug}
                    image={highlightNews.image}
                    imageSp={highlightNews.imageSp}
                    imageWebp={highlightNews.imageWebp}
                    imageWebpSp={highlightNews.imageWebpSp}
                    title={(highlightNews.headline as any)[i18n.language]}
                    summary={(highlightNews.topic as any)[i18n.language]}
                    onClick={handleNewsClick}
                  />
                </div>
              )}
            </div>
            <div className="t-media__info-news-carousel">
              <Carousel key={isMobileNews ? 'news-mobile' : 'news-pc'} mode="news">
                {newsCarouselList.map((news: any, index: number) => {
                    return (
                      <NewsCard
                        key={index}
                        value={news.slug}
                        image={news.image}
                        imageSp={news.imageSp}
                        imageWebp={news.imageWebp}
                        imageWebpSp={news.imageWebpSp}
                        title={(news.headline as any)[i18n.language]}
                        summary={(news.topic as any)[i18n.language]}
                        onClick={handleNewsClick}
                      />
                    );
                  })}
              </Carousel>
            </div>
          </Section>
        )}

        {currentTab === 1 && (
          <Section className={mapModifiers('t-media__info', 'gallery')}>
            <div className="t-media__info-gallery-tabs">
              <Tabs current={0} tabs={galleryTabs} onChange={onGalleryTabChange}>
                {galleryList.map((gallery, index) => (
                  <div key={index} className="t-media__info-gallery-tab">
                    <GalleryMasonry items={getGalleryItem(gallery.items)} />
                  </div>
                ))}
              </Tabs>
            </div>
          </Section>
        )}

        {currentTab === 2 && (
          <Section className={mapModifiers('t-media__info', 'ncc-group')}>
            <h2 className="t-media__info-ncc-group-title">{t('media.ncc-group.title')}</h2>
            <div className="t-media__info-ncc-group-description">
              <Text>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('media.ncc-group.description'),
                  }}
                />
              </Text>
              <Text>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('media.ncc-group.description-2'),
                  }}
                />
              </Text>
            </div>
            <div className="t-media__info-ncc-group-button">
              <Button size="large" href="http://www.nccthailand.com/en/" target="_blank">
                {t('media.ncc-group.explore')}
              </Button>
            </div>
          </Section>
        )}
      </Default>
    </div>
  );
};
